import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  Column,
  DataTableInput,
  DataTableOutput,
  Order,
  Search,
} from "@core/models/datables";
import { environment } from "environments/environment";
import { throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ProdFormula } from "../models/prod-formula";
import { TranslateService } from "@ngx-translate/core";
import { ProdFormulaVersionView } from "@ryd/models";

@Injectable({ providedIn: "root" })
export class ProdFormulaService {
  private lang: string = this._translateService.currentLang;
  private columns: Column[];
  private statusStage: Record<number, string> = {
    0: "ENP+RES+VAL+REC",
    1: "APR",
    2: "OBS+DES"
  }

  constructor(private _http: HttpClient, private _translateService: TranslateService) { }

  getColumns() {
    this._translateService.onLangChange.subscribe(value => {
      if (value) {
        this.lang = value.lang;
      }
    });
    this.columns = [
      {
        data: "id",
        name: "ID",
        searchable: false,
        orderable: true,
        search: new Search(),
      },
      {
        data: "code",
        name: "Code",
        searchable: true,
        orderable: true,
        search: new Search(),
      },
      {
        data: this.lang === "es" ? "description" : "descriptionEnglish",
        name: "Descripcion",
        searchable: true,
        orderable: true,
        search: new Search(),
      },
      {
        data: this.lang === "es" ? "productName" : "productNameEnglish",
        name: "Producto",
        searchable: true,
        orderable: true,
        search: new Search(),
      },
      {
        data: this.lang === "es" ? "processName" : "processNameEnglish",
        name: "Tipo de Proceso",
        searchable: true,
        orderable: true,
        search: new Search(),
      },
      {
        data: this.lang === "es" ? "modelName" : "modelNameEnglish",
        name: "Modelo de Formula",
        searchable: true,
        orderable: true,
        search: new Search(),
      },
      {
        data: "version",
        name: "Versión",
        searchable: false,
        orderable: true,
        search: new Search(),
      },
      {
        data: "dueDate",
        name: "Fecha vencimiento",
        searchable: false,
        orderable: false,
        search: new Search(),
      },
      {
        data: "statusCode",
        name: "Estado",
        searchable: true,
        orderable: true,
        search: {
          regex: true,
          value: this.statusStage[1],
        },
      },
    ];
  }


  /**
   * Gets formula's list wheter specific parameters
   * @param page page number
   * @param quantity register quantity
   * @param order object to indicates asc o desc
   * @param searchText filter text
   * @param stage Status stage where 0 means all status before APR, 1 means for APR and 2 means for OBS + DES
   * @returns 
   */
  getAllFormulas(
    page: number,
    quantity: number,
    order: Order,
    searchText: string,
    stage: number
  ) {
    const input = this.createDataTableInput(
      page,
      quantity,
      order,
      searchText,
      stage
    );
    return this._http.post<DataTableOutput<ProdFormula>>(
      `${environment.rydApiUrl}/api/formulas/v2/index`,
      input
    );
  }

  changeFormulaStatus(formulaId: number, statusId: number) {
    return this._http.put<{ message: String; formula: ProdFormula }>(
      `${environment.rydApiUrl}/api/formula/${formulaId}/${statusId}`,
      null
    );
  }

  saveFormula(formula: ProdFormula, code: String) {
    return this._http
      .post<ProdFormula>(`${environment.rydApiUrl}/api/formula/${code}`, formula)
      .pipe(
        map((respone: any) => respone.formula as ProdFormula),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  editFormula(formula: ProdFormula, code: String) {
    return this._http
      .put<ProdFormula>(
        `${environment.rydApiUrl}/api/formula/update/${formula.id}/${code}`,
        formula
      )
      .pipe(
        map((respone: any) => respone.formula as ProdFormula),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Funcion que busca una formula por su ID
   * @param id Identificador de formula
   * @returns retorna un entity response de formula
   */
  getById(id: number) {
    return this._http.get<ProdFormula>(
      `${environment.rydApiUrl}/api/formula/${id}`
    );
  }

  /**
   * Función que busca las formulas por medio del ID padre
   * @param id  ID de formula previa
   * @returns devuelve lista de formulas
   */
  getAllByFormulaPreviewId(id: number) {
    return this._http.get<ProdFormula[]>(
      `${environment.rydApiUrl}/api/formula/validate-preview/${id}`
    );
  }

  /**
   * Servicio para notificar por correo a todos los aprobadores de la formula
   * @param formula fórmula a notificar
   * @returns 
   */
  sendApproversNotification(formula: ProdFormula) {
    return this._http.put<ProdFormula>(`${environment.rydApiUrl}/api/formula/approvers-mail`, formula);
  }

  private createDataTableInput(
    page: number,
    quantity: number,
    order: Order,
    searchText: string,
    stage: number
  ): DataTableInput {
    this.getColumns();
    let columns = this.columns;
    let input = new DataTableInput();
    input.start = page * quantity;
    input.length = quantity;
    if (searchText !== "") {
      input.search.regex = true;
      input.search.value = searchText;
    }
    if (order) {
      input.order.push(order);
    }
    columns = columns.map((column) => {
      if (column.name === "Estado") {
        column.search.value = this.statusStage[stage];
      }
      return column;
    });
    input.columns = columns;
    return input;
  }


  /**
   * Devuelve el ultimo numero de prototipo activo para el producto especificado
   * @param productId el identificador del producto a consultar
   * @returns el numero de prototipo actual (version)
   */
  getLastPrototypeByProductId(productId: number) {
    return this._http.get<ProdFormulaVersionView>(`${environment.rydApiUrl}/api/formula/version/${productId}`).toPromise();
  }


  /**
  * Devuelve el ultimo numero de versión activo para el producto especificado
  * @param productId el identificador del producto a consultar
  * @returns el numero de prototipo actual (version) y el id de la fórmula
  */
  getLastVersionByProductId(productId: number) {
    return this._http.get<ProdFormulaVersionView>(`${environment.rydApiUrl}/api/formula/formula-version/${productId}`).toPromise();
  }

  getAllFormulasByProductId(productId: number) {
    return this._http.get<ProdFormula[]>(`${environment.rydApiUrl}/api/formula/${productId}/products`);
  }

}
